import { polygonRings } from "@constructionautomation/backend/convex/lib/measurementGeometry";
import { getPolygonScreenPath } from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Check } from "lucide-react";

import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { DEFAULT_TEXT_FONT_SIZE } from "@/hooks/useMeasurementTextLock";
import { formatMeasurementHeight } from "@/lib/geometry";
import { DEFAULT_TAG_COLOR } from "@/lib/tag-colors";
import { TOOL_IDS } from "@/viewport-tools/measurement";

import {
  buildLabelGroupMembersById,
  isLabelGroupAnchor,
  isLabelGroupMember,
  shouldShowMeasurementValue,
} from "@/viewport-tools/measurement/overlay/measurement-label-groups";
import {
  computeValue,
  formatDisplayedMeasurement,
  getMeasurementLabelPosition,
  getMeasurementLeaderAnchor,
  hexToRgba,
  shouldShowMeasurementLeader,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  type DraftMeasurementState,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import type { InteractionState, LabelResizeHandle, ResizeHandle } from "@/viewport-tools/measurement/overlay/interactions/types";
import {
  MIN_LABEL_TEXT_BOX_WIDTH_SCREEN_PX,
  pageWidthToScreenLength,
  screenLengthToPageWidth,
} from "@/viewport-tools/measurement/overlay/interactions/transform-interactions";
import {
  getMeasurementLabelCardStyle,
  getMeasurementLabelScale,
  getScreenPxPerPagePoint,
  MEASUREMENT_LABEL_CARD_CLASS_NAME,
  MeasurementLabelCardContent,
} from "@/viewport-tools/measurement/overlay/measurement-label-card";
import { cn } from "@/lib/utils";

function isOpenPathTool(tool: MeasurementOverlayRecord["tool"] | DraftMeasurementState["tool"]) {
  return tool === TOOL_IDS.line || tool === TOOL_IDS.polyline;
}

function MeasurementLabelButton({
  labelRef,
  label,
  labelFontSize,
  labelScale,
  labelPosition,
  screenRect,
  borderColor,
  isSelected,
  measurement,
  annotation,
  annotationLine,
  displayedValue,
  showValues,
  textBoxWidthCssPx,
  resolveInitialPageWidth,
  startMeasurementLabelMove,
  startMeasurementLabelResize,
  openContextMenu,
}: {
  labelRef: RefObject<HTMLButtonElement | null>;
  label: string;
  labelFontSize: number;
  labelScale: number;
  labelPosition: { x: number; y: number };
  screenRect?: { x: number; y: number };
  borderColor: string;
  isSelected: boolean;
  measurement: MeasurementOverlayRecord;
  annotation?: MeasurementAnnotation;
  annotationLine?: string;
  displayedValue: number;
  showValues?: boolean;
  /** Pre-scale CSS width in px when annotation.textBoxWidth is set. */
  textBoxWidthCssPx?: number;
  resolveInitialPageWidth: (renderedScreenWidthPx: number) => number;
  startMeasurementLabelMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation | undefined
  ) => void;
  startMeasurementLabelResize: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    handle: LabelResizeHandle,
    annotation: MeasurementAnnotation | undefined,
    initialWidth: number
  ) => void;
  openContextMenu: (event: ReactMouseEvent, measurement: MeasurementOverlayRecord) => void;
}) {
  const offsetX = screenRect?.x ?? 0;
  const offsetY = screenRect?.y ?? 0;
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: labelPosition.x - offsetX,
        top: labelPosition.y - offsetY,
        transform: `translate(-50%, -50%) scale(${labelScale})`,
        transformOrigin: "center center",
      }}
    >
      <button
        ref={labelRef}
        type="button"
        aria-label={`Move ${label} label`}
        className={cn(
          "relative pointer-events-auto",
          MEASUREMENT_LABEL_CARD_CLASS_NAME
        )}
        style={{
          ...getMeasurementLabelCardStyle({
            borderColor,
            fontSize: labelFontSize,
            textBoxWidthCssPx,
          }),
          cursor: isSelected ? "grab" : "pointer",
        }}
        onMouseDown={(event) => startMeasurementLabelMove(event, measurement._id, annotation)}
        onContextMenu={(event) => openContextMenu(event, measurement)}
      >
        <MeasurementLabelCardContent
          label={label}
          annotationLine={
            measurement.aiQuantityPending
              ? "Needs calibration · Needs review"
              : measurement.aiReviewStatus === "pending_review"
                ? "Needs review"
                : annotationLine
          }
          valueLine={
            showValues !== false && !measurement.aiQuantityPending
              ? `${formatDisplayedMeasurement(measurement.measurementType, displayedValue)} ${measurement.measurementType}`
              : undefined
          }
        />
      </button>
      {isSelected
        ? (["w", "e"] as const).map((handle) => (
            <button
              key={handle}
              type="button"
              aria-label={`Resize ${label} label (${handle})`}
              className="absolute top-1/2 z-10 size-2.5 -translate-y-1/2 rounded-full border-2 border-background bg-sky-500 pointer-events-auto"
              style={{
                left: handle === "w" ? -5 : undefined,
                right: handle === "e" ? -5 : undefined,
                cursor: "ew-resize",
              }}
              onMouseDown={(event) => {
                // Only measure the DOM when the label has no stored width to start from.
                const resolveUnsetInitialWidth = () => {
                  const scale = Math.max(0.01, Math.abs(labelScale));
                  const node = labelRef.current;
                  const renderedScreenWidthPx = node
                    ? node.offsetWidth * scale
                    : (textBoxWidthCssPx ?? MIN_LABEL_TEXT_BOX_WIDTH_SCREEN_PX) * scale;
                  return resolveInitialPageWidth(renderedScreenWidthPx);
                };
                startMeasurementLabelResize(
                  event,
                  measurement._id,
                  handle,
                  annotation,
                  annotation?.textBoxWidth ?? resolveUnsetInitialWidth()
                );
              }}
            />
          ))
        : null}
    </div>
  );
}

interface PersistedMeasurementsLayerProps {
  viewport: DocumentViewportValue;
  measurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  previewAnnotations: Record<string, MeasurementAnnotation>;
  previewGeometries: Record<string, MeasurementGeometry | PolygonGeometry>;
  colorByTagId: Map<string, string>;
  labelByTagId: Map<string, string>;
  annotationByTagId?: Map<string, string>;
  scale: MeasurementScale | null;
  valueIncrementFeet?: number;
  textLocked?: boolean;
  textFontSize?: number;
  showLabels?: boolean;
  showLeaders?: boolean;
  showValues?: boolean;
  startMeasurementMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry
  ) => void;
  startMeasurementResize: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    handle: ResizeHandle,
    geometry: MeasurementGeometry
  ) => void;
  startMeasurementVertexDrag: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number,
    geometry: PolygonGeometry
  ) => void;
  openVertexContextMenu: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number
  ) => void;
  startMeasurementLabelMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation | undefined
  ) => void;
  startMeasurementLabelResize: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    handle: LabelResizeHandle,
    annotation: MeasurementAnnotation | undefined,
    initialWidth: number
  ) => void;
  openContextMenu: (
    event: ReactMouseEvent,
    measurement: MeasurementOverlayRecord
  ) => void;
}

interface PersistedMeasurementItemProps {
  viewport: DocumentViewportValue;
  measurement: MeasurementOverlayRecord;
  isSelected: boolean;
  previewAnnotation?: MeasurementAnnotation;
  previewGeometry?: MeasurementGeometry | PolygonGeometry;
  /** When set, label position comes from the group anchor (shared label). */
  sharedLabelSource?: {
    geometry: MeasurementGeometry | PolygonGeometry;
    annotation?: MeasurementAnnotation;
  };
  /** Render the label card (false for non-anchor group members). */
  renderLabelCard?: boolean;
  /** True when this measurement is in a multi-member label group on the page. */
  inActiveLabelGroup?: boolean;
  /** Shared card size from the group anchor (for member leader edge targeting). */
  sharedLabelSize?: { width: number; height: number };
  /** Report measured label card size (anchors only). */
  onLabelSizeChange?: (measurementId: string, size: { width: number; height: number }) => void;
  colorByTagId: Map<string, string>;
  labelByTagId: Map<string, string>;
  annotationByTagId?: Map<string, string>;
  scale: MeasurementScale | null;
  valueIncrementFeet?: number;
  textLocked?: boolean;
  textFontSize?: number;
  showLabels?: boolean;
  showLeaders?: boolean;
  showValues?: boolean;
  startMeasurementMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry
  ) => void;
  startMeasurementResize: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    handle: ResizeHandle,
    geometry: MeasurementGeometry
  ) => void;
  startMeasurementVertexDrag: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number,
    geometry: PolygonGeometry
  ) => void;
  openVertexContextMenu: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number
  ) => void;
  startMeasurementLabelMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation | undefined
  ) => void;
  startMeasurementLabelResize: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    handle: LabelResizeHandle,
    annotation: MeasurementAnnotation | undefined,
    initialWidth: number
  ) => void;
  openContextMenu: (
    event: ReactMouseEvent,
    measurement: MeasurementOverlayRecord
  ) => void;
}

function useSharedMeasurementState(props: PersistedMeasurementItemProps) {
  const {
    viewport,
    measurement,
    isSelected,
    previewAnnotation,
    previewGeometry,
    sharedLabelSource,
    inActiveLabelGroup = false,
    sharedLabelSize,
    onLabelSizeChange,
    colorByTagId,
    labelByTagId,
    annotationByTagId,
    scale,
    valueIncrementFeet,
    textLocked,
    textFontSize,
    showLeaders,
    showValues,
  } = props;

  const tagKey = String(measurement.tagId ?? "");
  const color = colorByTagId.get(tagKey) ?? DEFAULT_TAG_COLOR;
  const label = labelByTagId.get(tagKey) ?? "Tag";
  const annotationLine = annotationByTagId?.get(tagKey);
  const fillColor = hexToRgba(color, isSelected ? 0.14 : 0.1);
  const hatchColor = hexToRgba(color, 0.22);
  const geometry = previewGeometry ?? measurement.geometry;
  const annotation = previewAnnotation ?? measurement.annotation;
  const labelGeometry = sharedLabelSource?.geometry ?? geometry;
  const labelAnnotation = sharedLabelSource?.annotation ?? annotation;
  const displayedValue =
    scale && previewGeometry
      ? computeValue(
          measurement.tool,
          measurement.measurementType,
          previewGeometry,
          measurement.height,
          scale.pixelsPerFoot,
          scale.pageAspectRatio,
          valueIncrementFeet
        )
      : measurement.value;
  const labelPositionPage = getMeasurementLabelPosition(labelGeometry, labelAnnotation);
  const isOpenPath = geometry.type === MEASUREMENT_GEOMETRY_TYPES.poly && isOpenPathTool(measurement.tool);
  const leaderAnchorPage = getMeasurementLeaderAnchor(geometry, labelPositionPage, {
    closed: !isOpenPath,
  });
  const labelAnchor = viewport.transforms.pageToScreenPoint(leaderAnchorPage);
  const labelPosition = viewport.transforms.pageToScreenPoint(labelPositionPage);
  // Group leaders use the shared label's leaderVisible (anchor annotation).
  const leaderAnnotation = sharedLabelSource ? labelAnnotation : annotation;
  const showLeader = showLeaders !== false && shouldShowMeasurementLeader(leaderAnnotation);
  const effectiveShowValues = shouldShowMeasurementValue(showValues, inActiveLabelGroup);
  const { baseFontSize: labelFontSize, scale: labelScale } = getMeasurementLabelScale(
    textFontSize ?? DEFAULT_TEXT_FONT_SIZE,
    getScreenPxPerPagePoint(viewport),
    viewport.camera.zoom,
    textLocked ?? false
  );
  const labelRef = useRef<HTMLButtonElement | null>(null);
  const [labelSize, setLabelSize] = useState({ width: 0, height: 0 });
  const textBoxWidthCssPx =
    labelAnnotation?.textBoxWidth !== undefined && labelAnnotation.textBoxWidth > 0
      ? pageWidthToScreenLength(labelAnnotation.textBoxWidth, viewport.transforms.pageToScreenPoint) /
        Math.max(0.01, Math.abs(labelScale))
      : undefined;

  useLayoutEffect(() => {
    const node = labelRef.current;
    if (!node) {
      return;
    }
    const nextSize = { width: node.offsetWidth, height: node.offsetHeight };
    setLabelSize((current) =>
      current.width === nextSize.width && current.height === nextSize.height ? current : nextSize
    );
    onLabelSizeChange?.(String(measurement._id), nextSize);
  }, [
    label,
    annotationLine,
    displayedValue,
    effectiveShowValues,
    measurement.measurementType,
    measurement._id,
    labelFontSize,
    labelScale,
    textBoxWidthCssPx,
    onLabelSizeChange,
  ]);

  const resolveInitialPageWidth = (renderedScreenWidthPx: number) =>
    screenLengthToPageWidth(renderedScreenWidthPx, viewport.transforms.pageToScreenPoint);

  const resolvedLabelSize =
    labelSize.width > 0 || labelSize.height > 0
      ? labelSize
      : (sharedLabelSize ?? labelSize);

  const leaderTarget = resolveLeaderTargetPoint({
    anchor: labelAnchor,
    labelCenter: labelPosition,
    labelWidth: resolvedLabelSize.width * Math.max(0.01, Math.abs(labelScale)),
    labelHeight: resolvedLabelSize.height * Math.max(0.01, Math.abs(labelScale)),
  });

  return {
    tagKey,
    color,
    label,
    annotationLine,
    fillColor,
    hatchColor,
    geometry,
    annotation,
    labelAnnotation,
    displayedValue,
    labelPositionPage,
    isOpenPath,
    leaderAnchorPage,
    labelAnchor,
    labelPosition,
    showLeader,
    effectiveShowValues,
    labelFontSize,
    labelScale,
    labelRef,
    textBoxWidthCssPx,
    resolveInitialPageWidth,
    leaderTarget,
  };
}

function RectMeasurementItem({
  viewport,
  measurement,
  isSelected,
  color,
  label,
  fillColor,
  hatchColor,
  geometry,
  annotation,
  showLeader,
  showLabels,
  labelAnchor,
  leaderTarget,
  labelButtonProps,
  startMeasurementMove,
  startMeasurementResize,
  openContextMenu,
}: {
  viewport: DocumentViewportValue;
  measurement: MeasurementOverlayRecord;
  isSelected: boolean;
  color: string;
  label: string;
  fillColor: string;
  hatchColor: string;
  geometry: MeasurementGeometry;
  annotation: MeasurementAnnotation | undefined;
  showLeader: boolean;
  showLabels: boolean | undefined;
  labelAnchor: { x: number; y: number };
  leaderTarget: { x: number; y: number };
  labelButtonProps: Omit<React.ComponentProps<typeof MeasurementLabelButton>, "screenRect">;
  startMeasurementMove: PersistedMeasurementItemProps["startMeasurementMove"];
  startMeasurementResize: PersistedMeasurementItemProps["startMeasurementResize"];
  openContextMenu: PersistedMeasurementItemProps["openContextMenu"];
}) {
  const rect = geometry;
  const screenRect = viewport.transforms.pageRectToScreenRect({
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  });

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: screenRect.x,
        top: screenRect.y,
        width: screenRect.width,
        height: screenRect.height,
      }}
    >
      <button
        type="button"
        className="absolute inset-0 pointer-events-auto rounded-none border-2 cursor-move"
        style={{
          borderColor: color,
          backgroundColor: fillColor,
          backgroundImage: `repeating-linear-gradient(135deg, ${hatchColor} 0 2px, transparent 2px 10px)`,
          boxShadow: isSelected ? `0 0 0 2px ${color}55` : `0 0 0 1px ${color}33`,
        }}
        onMouseDown={(event) => startMeasurementMove(event, measurement._id, rect)}
        onContextMenu={(event) => openContextMenu(event, measurement)}
      />
      {showLeader ? (
        <svg className="absolute inset-0 h-full w-full pointer-events-none overflow-visible">
          {renderLeaderArrow({
            startX: labelAnchor.x - screenRect.x,
            startY: labelAnchor.y - screenRect.y,
            endX: leaderTarget.x - screenRect.x,
            endY: leaderTarget.y - screenRect.y,
            color,
            strokeWidth: (annotation?.strokeWidth ?? 2) * 0.5,
          })}
        </svg>
      ) : null}
      {showLabels !== false ? (
        <MeasurementLabelButton {...labelButtonProps} screenRect={screenRect} />
      ) : null}
      {isSelected
        ? (["nw", "ne", "sw", "se"] as const).map((handle) => {
            const handlePosition = {
              nw: { left: -6, top: -6, cursor: "nwse-resize" },
              ne: { right: -6, top: -6, cursor: "nesw-resize" },
              sw: { left: -6, bottom: -6, cursor: "nesw-resize" },
              se: { right: -6, bottom: -6, cursor: "nwse-resize" },
            }[handle];
            return (
              <button
                key={handle}
                type="button"
                aria-label={`Resize ${label} measurement ${measurement._id} (${handle})`}
                className="absolute pointer-events-auto size-3 rounded-full border-2 border-background bg-sky-500"
                style={handlePosition}
                onMouseDown={(event) =>
                  startMeasurementResize(event, measurement._id, handle, rect)
                }
              />
            );
          })
        : null}
    </div>
  );
}

function PolyMeasurementItem({
  viewport,
  measurement,
  isSelected,
  color,
  label,
  fillColor,
  geometry,
  annotation,
  isOpenPath,
  showLeader,
  showLabels,
  labelAnchor,
  leaderTarget,
  labelButtonProps,
  startMeasurementMove,
  startMeasurementVertexDrag,
  openVertexContextMenu,
  openContextMenu,
}: {
  viewport: DocumentViewportValue;
  measurement: MeasurementOverlayRecord;
  isSelected: boolean;
  color: string;
  label: string;
  fillColor: string;
  geometry: PolygonGeometry;
  annotation: MeasurementAnnotation | undefined;
  isOpenPath: boolean;
  showLeader: boolean;
  showLabels: boolean | undefined;
  labelAnchor: { x: number; y: number };
  leaderTarget: { x: number; y: number };
  labelButtonProps: Omit<React.ComponentProps<typeof MeasurementLabelButton>, "screenRect">;
  startMeasurementMove: PersistedMeasurementItemProps["startMeasurementMove"];
  startMeasurementVertexDrag: PersistedMeasurementItemProps["startMeasurementVertexDrag"];
  openVertexContextMenu: PersistedMeasurementItemProps["openVertexContextMenu"];
  openContextMenu: PersistedMeasurementItemProps["openContextMenu"];
}) {
  const poly = geometry;
  const screenVertices = poly.vertices.map((v) =>
    viewport.transforms.pageToScreenPoint(v)
  );

  const isClosed = !isOpenPath && screenVertices.length >= 3;
  const pathData = getPolygonScreenPath(poly, viewport, isClosed);
  const handles = polygonRings(poly).flatMap((ring, ringIndex) => ring.map((point, index) => ({
    point: viewport.transforms.pageToScreenPoint(point),
    label: ringIndex === 0 ? `vertex ${index + 1}` : `cutout ${ringIndex} vertex ${index + 1}`,
  })));

  const handlePolyMouseDown = (event: Parameters<typeof startMeasurementMove>[0]) =>
    startMeasurementMove(event, measurement._id, poly);

  return (
    <div className="absolute inset-0 pointer-events-none">
      <svg className="absolute inset-0 w-full h-full">
        {isOpenPathTool(measurement.tool) ? (
          <path
            d={pathData}
            fill="none"
            stroke="transparent"
            strokeWidth="14"
            strokeLinecap="round"
            className="cursor-move pointer-events-auto"
            onMouseDown={handlePolyMouseDown}
            onContextMenu={(event) => openContextMenu(event, measurement)}
          />
        ) : null}
        <path
          d={pathData}
          fill={isClosed ? fillColor : "none"}
          fillRule="evenodd"
          stroke={color}
          strokeWidth={annotation?.strokeWidth ?? 2}
          className={
            isOpenPathTool(measurement.tool)
              ? "pointer-events-none"
              : "cursor-move pointer-events-auto"
          }
          strokeDasharray={
            measurement.aiReviewStatus === "pending_review"
              ? "6,4"
              : isSelected
                ? "none"
                : "5,5"
          }
          onMouseDown={
            isOpenPathTool(measurement.tool) ? undefined : handlePolyMouseDown
          }
          onContextMenu={(event) => openContextMenu(event, measurement)}
        />
        {showLeader
          ? renderLeaderArrow({
              startX: labelAnchor.x,
              startY: labelAnchor.y,
              endX: leaderTarget.x,
              endY: leaderTarget.y,
              color,
              strokeWidth: (annotation?.strokeWidth ?? 2) * 0.5,
            })
          : null}
      </svg>
      {isSelected
        ? handles.map(({ point, label: handleLabel }, index) => (
            <button
              key={`${measurement._id}-vertex-${index}`}
              type="button"
              aria-label={`Adjust ${label} ${handleLabel}`}
              className="absolute pointer-events-auto size-3 rounded-full border border-white bg-sky-500 shadow-[0_0_0_1px_rgba(2,132,199,0.55)]"
              style={{
                left: point.x,
                top: point.y,
                transform: "translate(-50%, -50%)",
                cursor: "move",
              }}
              onMouseDown={(event) => {
                if (event.button !== 0) {
                  return;
                }
                startMeasurementVertexDrag(event, measurement._id, index, poly);
              }}
              onContextMenu={(event) => openVertexContextMenu(event, measurement._id, index)}
            />
          ))
        : null}
      {showLabels !== false ? (
        <MeasurementLabelButton {...labelButtonProps} />
      ) : null}
    </div>
  );
}

function PersistedMeasurementItem(props: PersistedMeasurementItemProps) {
  const {
    viewport,
    measurement,
    isSelected,
    showLabels,
    showLeaders: _showLeaders,
    renderLabelCard = true,
    inActiveLabelGroup = false,
    startMeasurementMove,
    startMeasurementResize,
    startMeasurementVertexDrag,
    openVertexContextMenu,
    startMeasurementLabelMove,
    startMeasurementLabelResize,
    openContextMenu,
  } = props;
  const state = useSharedMeasurementState(props);
  const {
    showLeader,
    color,
    label,
    fillColor,
    hatchColor,
    geometry,
    annotation,
    labelFontSize,
    labelScale,
    labelPosition,
    labelAnchor,
    leaderTarget,
  } = state;

  // Label edits always target the shared-label owner (anchor when grouped).
  const labelOwnerId =
    inActiveLabelGroup && measurement.labelGroupId
      ? (measurement.labelGroupId as Id<"takeoffPilotMeasurements">)
      : measurement._id;

  const labelButtonProps: Omit<React.ComponentProps<typeof MeasurementLabelButton>, "screenRect"> = {
    labelRef: state.labelRef,
    label,
    labelFontSize,
    labelScale,
    labelPosition,
    borderColor: color,
    isSelected,
    measurement: { ...measurement, _id: labelOwnerId },
    annotation: state.labelAnnotation,
    annotationLine: state.annotationLine,
    displayedValue: state.displayedValue,
    showValues: state.effectiveShowValues,
    textBoxWidthCssPx: state.textBoxWidthCssPx,
    resolveInitialPageWidth: state.resolveInitialPageWidth,
    startMeasurementLabelMove,
    startMeasurementLabelResize,
    openContextMenu,
  };

  const showLabelCard = showLabels !== false && renderLabelCard;

  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    return (
      <RectMeasurementItem
        viewport={viewport}
        measurement={measurement}
        isSelected={isSelected}
        color={color}
        label={label}
        fillColor={fillColor}
        hatchColor={hatchColor}
        geometry={geometry}
        annotation={annotation}
        showLeader={showLeader}
        showLabels={showLabelCard}
        labelAnchor={labelAnchor}
        leaderTarget={leaderTarget}
        labelButtonProps={labelButtonProps}
        startMeasurementMove={startMeasurementMove}
        startMeasurementResize={startMeasurementResize}
        openContextMenu={openContextMenu}
      />
    );
  }

  return (
    <PolyMeasurementItem
      viewport={viewport}
      measurement={measurement}
      isSelected={isSelected}
      color={color}
      label={label}
      fillColor={fillColor}
      geometry={geometry as PolygonGeometry}
      annotation={annotation}
      isOpenPath={geometry.type === MEASUREMENT_GEOMETRY_TYPES.poly && isOpenPathTool(measurement.tool)}
      showLeader={showLeader}
      showLabels={showLabelCard}
      labelAnchor={labelAnchor}
      leaderTarget={leaderTarget}
      labelButtonProps={labelButtonProps}
      startMeasurementMove={startMeasurementMove}
      startMeasurementVertexDrag={startMeasurementVertexDrag}
      openVertexContextMenu={openVertexContextMenu}
      openContextMenu={openContextMenu}
    />
  );
}

export function PersistedMeasurementsLayer({
  viewport,
  measurements,
  selectedMeasurementIds,
  previewAnnotations,
  previewGeometries,
  colorByTagId,
  labelByTagId,
  annotationByTagId,
  scale,
  valueIncrementFeet,
  textLocked,
  textFontSize,
  showLabels,
  showLeaders,
  showValues,
  startMeasurementMove,
  startMeasurementResize,
  startMeasurementVertexDrag,
  openVertexContextMenu,
  startMeasurementLabelMove,
  startMeasurementLabelResize,
  openContextMenu,
}: PersistedMeasurementsLayerProps) {
  const groupMembersById = buildLabelGroupMembersById(measurements);
  const measurementById = new Map(
    measurements.map((measurement) => [String(measurement._id), measurement] as const)
  );
  const [anchorLabelSizes, setAnchorLabelSizes] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const handleLabelSizeChange = useCallback((measurementId: string, size: { width: number; height: number }) => {
    setAnchorLabelSizes((current) => {
      const prev = current[measurementId];
      if (prev && prev.width === size.width && prev.height === size.height) {
        return current;
      }
      return { ...current, [measurementId]: size };
    });
  }, []);

  return (
    <>
      {measurements.map((measurement) => {
        const isSelected = selectedMeasurementIds.includes(measurement._id);
        const groupMembers = measurement.labelGroupId
          ? groupMembersById.get(measurement.labelGroupId)
          : undefined;
        const inActiveGroup = !!groupMembers && groupMembers.length >= 2;
        const anchor = inActiveGroup
          ? measurementById.get(measurement.labelGroupId!)
          : undefined;
        const sharedLabelSource =
          inActiveGroup && anchor
            ? {
                geometry:
                  previewGeometries[String(anchor._id)] ?? anchor.geometry,
                annotation:
                  previewAnnotations[String(anchor._id)] ?? anchor.annotation,
              }
            : undefined;
        const renderLabelCard =
          !inActiveGroup || isLabelGroupAnchor(measurement) || !isLabelGroupMember(measurement);
        const sharedLabelSize =
          inActiveGroup && measurement.labelGroupId
            ? anchorLabelSizes[measurement.labelGroupId]
            : undefined;

        return (
          <PersistedMeasurementItem
            key={measurement._id}
            viewport={viewport}
            measurement={measurement}
            isSelected={isSelected}
            previewAnnotation={previewAnnotations[String(measurement._id)]}
            previewGeometry={previewGeometries[String(measurement._id)]}
            sharedLabelSource={sharedLabelSource}
            renderLabelCard={renderLabelCard}
            inActiveLabelGroup={inActiveGroup}
            sharedLabelSize={sharedLabelSize}
            onLabelSizeChange={renderLabelCard ? handleLabelSizeChange : undefined}
            colorByTagId={colorByTagId}
            labelByTagId={labelByTagId}
            annotationByTagId={annotationByTagId}
            scale={scale}
            valueIncrementFeet={valueIncrementFeet}
            textLocked={textLocked}
            textFontSize={textFontSize}
            showLabels={showLabels}
            showLeaders={showLeaders}
            showValues={showValues}
            startMeasurementMove={startMeasurementMove}
            startMeasurementResize={startMeasurementResize}
            startMeasurementVertexDrag={startMeasurementVertexDrag}
            openVertexContextMenu={openVertexContextMenu}
            startMeasurementLabelMove={startMeasurementLabelMove}
            startMeasurementLabelResize={startMeasurementLabelResize}
            openContextMenu={openContextMenu}
          />
        );
      })}
    </>
  );
}

export function DraftMeasurementPreviewLayer({
  viewport,
  drawingDraft,
  draftMeasurement,
  interaction,
  startDraftMove,
}: {
  viewport: DocumentViewportValue;
  drawingDraft: DraftMeasurementState | null;
  draftMeasurement: DraftMeasurementState | null;
  interaction: InteractionState | null;
  startDraftMove: (event: ReactMouseEvent, draft: DraftMeasurementState) => void;
}) {
  const draft = drawingDraft ?? draftMeasurement;
  if (!draft) {
    return null;
  }

  const color = DEFAULT_TAG_COLOR;
  const fillColor = hexToRgba(color, 0.1);
  const hatchColor = hexToRgba(color, 0.22);

  if (draft.geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    const rect = draft.geometry;
    const screenRect = viewport.transforms.pageRectToScreenRect({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    });

    return (
      <div
        className="absolute rounded-none border-2 border-dashed border-sky-500"
        style={{
          left: screenRect.x,
          top: screenRect.y,
          width: screenRect.width,
          height: screenRect.height,
          backgroundColor: fillColor,
          backgroundImage: `repeating-linear-gradient(135deg, ${hatchColor} 0 2px, transparent 2px 10px)`,
          pointerEvents: drawingDraft ? "none" : "auto",
          cursor: drawingDraft ? "default" : "move",
        }}
        onMouseDown={(event) => startDraftMove(event, draft)}
      >
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <div
            className="text-center text-[13px] font-black leading-tight text-sky-900"
            style={{ textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}
          >
            <div>
              {formatDisplayedMeasurement(draft.measurementType, draft.value)}{" "}
              {draft.measurementType}
            </div>
            {draft.height ? <div>Height: {formatMeasurementHeight(draft.height)}</div> : null}
          </div>
        </div>
      </div>
    );
  }

  const poly = draft.geometry;
  if (poly.vertices.length < 1) {
    return null;
  }

  const screenVertices = poly.vertices.map((v) =>
    viewport.transforms.pageToScreenPoint(v)
  );
  const lineFinishIndex =
    isOpenPathTool(draft.tool) && interaction?.mode === "drawing"
      ? Math.max(0, poly.vertices.length - 2)
      : -1;
  const vertexMarkers = screenVertices.map((point, index) => {
    const isPolygonStart =
      draft.tool === TOOL_IDS.polygon && index === 0 && poly.vertices.length >= 3;
    const isLineFinish =
      isOpenPathTool(draft.tool) &&
      index === lineFinishIndex &&
      poly.vertices.length >= 2;
    return (
      <div
        key={`vertex-${index}-${point.x}-${point.y}`}
        className="absolute pointer-events-none"
        style={{
          left: point.x,
          top: point.y,
          transform: "translate(-50%, -50%)",
        }}
      >
        <div className="size-2.5 rounded-full border border-white bg-sky-500 shadow-[0_0_0_1px_rgba(2,132,199,0.55)]" />
        {isPolygonStart || isLineFinish ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_0_0_1px_rgba(16,185,129,0.75)]">
            <Check className="size-2" />
          </div>
        ) : null}
      </div>
    );
  });

  if (poly.vertices.length < 2) {
    return <>{vertexMarkers}</>;
  }

  const isClosed = !isOpenPathTool(draft.tool) && poly.vertices.length >= 3;
  const pathData = getPolygonScreenPath(poly, viewport, isClosed);

  const labelPosition = viewport.transforms.pageToScreenPoint(
    getMeasurementLabelPosition(draft.geometry, draft.annotation)
  );

  return (
    <div className="absolute inset-0 pointer-events-none">
      <svg className="absolute inset-0 w-full h-full">
        <path
          d={pathData}
          fill={isClosed ? fillColor : "none"}
          fillRule="evenodd"
          stroke={color}
          strokeWidth="2"
          strokeDasharray="5,5"
          className={drawingDraft ? undefined : "pointer-events-auto"}
          style={{ cursor: drawingDraft ? "default" : "move" }}
          onMouseDown={(event) => startDraftMove(event, draft)}
        />
      </svg>
      {vertexMarkers}
      <div
        className="absolute text-center text-[13px] font-black leading-tight"
        style={{
          left: labelPosition.x,
          top: labelPosition.y,
          transform: "translate(-50%, -50%)",
          color: "#0c4a6e",
          textShadow: "0 1px 0 rgba(255,255,255,0.35)",
        }}
      >
        <div>
          {formatDisplayedMeasurement(draft.measurementType, draft.value)}{" "}
          {draft.measurementType}
        </div>
        {draft.height ? <div>Height: {formatMeasurementHeight(draft.height)}</div> : null}
      </div>
    </div>
  );
}

export function BoxSelectionLayer({
  viewport,
  interaction,
}: {
  viewport: DocumentViewportValue;
  interaction: InteractionState | null;
}) {
  if (interaction?.mode !== "box-select") {
    return null;
  }

  const start = interaction.startPoint;
  const current = interaction.currentPoint;
  const screenRect = viewport.transforms.pageRectToScreenRect({
    x: Math.min(start.x, current.x),
    y: Math.min(start.y, current.y),
    width: Math.abs(current.x - start.x),
    height: Math.abs(current.y - start.y),
  });

  return (
    <div
      className="absolute pointer-events-none rounded-sm border-2 border-dashed border-sky-500 bg-sky-500/10 shadow-[0_0_0_1px_rgba(255,255,255,0.55)]"
      style={{
        left: screenRect.x,
        top: screenRect.y,
        width: screenRect.width,
        height: screenRect.height,
      }}
    />
  );
}

export type { PersistedMeasurementsLayerProps };

function renderLeaderArrow(args: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  color: string;
  strokeWidth: number;
}) {
  const dx = args.endX - args.startX;
  const dy = args.endY - args.startY;
  const length = Math.hypot(dx, dy);
  if (length < 1) {
    return null;
  }

  const ux = dx / length;
  const uy = dy / length;
  const gap = 2;
  const arrowSize = 6;
  const shaftEndX = args.endX - ux * (gap + arrowSize + 1);
  const shaftEndY = args.endY - uy * (gap + arrowSize + 1);
  const arrowTipX = args.endX - ux * gap;
  const arrowTipY = args.endY - uy * gap;
  const perpX = -uy;
  const perpY = ux;
  const leftX = arrowTipX - ux * arrowSize + perpX * (arrowSize * 0.55);
  const leftY = arrowTipY - uy * arrowSize + perpY * (arrowSize * 0.55);
  const rightX = arrowTipX - ux * arrowSize - perpX * (arrowSize * 0.55);
  const rightY = arrowTipY - uy * arrowSize - perpY * (arrowSize * 0.55);

  return (
    <>
      <line
        x1={args.startX}
        y1={args.startY}
        x2={shaftEndX}
        y2={shaftEndY}
        stroke={args.color}
        strokeWidth={args.strokeWidth}
        className="pointer-events-none"
      />
      <path
        d={`M ${leftX} ${leftY} L ${arrowTipX} ${arrowTipY} L ${rightX} ${rightY} Z`}
        fill={args.color}
        stroke={args.color}
        strokeWidth={args.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="pointer-events-none"
      />
    </>
  );
}

function resolveLeaderTargetPoint(args: {
  anchor: { x: number; y: number };
  labelCenter: { x: number; y: number };
  labelWidth: number;
  labelHeight: number;
}) {
  const halfWidth = args.labelWidth / 2;
  const halfHeight = args.labelHeight / 2;
  if (halfWidth <= 0 || halfHeight <= 0) {
    return args.labelCenter;
  }

  const dx = args.anchor.x - args.labelCenter.x;
  const dy = args.anchor.y - args.labelCenter.y;
  const ratio = Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return args.labelCenter;
  }

  return {
    x: args.labelCenter.x + dx / ratio,
    y: args.labelCenter.y + dy / ratio,
  };
}
